/**
 * Configuración resuelta en RUNTIME, no en compilación.
 *
 * Vite congela las variables `VITE_*` DENTRO del bundle, así que una imagen de
 * SPA era específica de entorno: la misma aplicación apuntando a otra API
 * exigía reconstruirla. Esto la despega. En producción, el nginx que sirve el
 * bundle genera `/config.json` con `envsubst` al arrancar el contenedor, y aquí
 * se lee ANTES de montar nada.
 *
 * En el servidor de desarrollo de Vite ese fichero no existe: la carpeta
 * `public/` solo lleva el icono de pestaña, y `config.json` no se sirve desde
 * ahí ni aquí ni en producción —lo escribe el entrypoint del contenedor—. Sin
 * fichero no se guarda nada y todo cae a `import.meta.env`. El flujo local queda exactamente como estaba, incluido el
 * conmutador mock/live en caliente.
 *
 * Precedencia, de más fuerte a más débil:
 *
 *   localStorage  ->  lo que haya elegido a mano quien está delante
 *   config.json   ->  lo que dice el contenedor
 *   import.meta.env -> lo que se horneó al compilar
 *   el valor seguro por defecto
 *
 * `localStorage` va arriba del todo a propósito: es el conmutador de la barra
 * superior, y si la configuración del servidor lo pisara dejaría de servir para
 * nada.
 */

export interface ConfigRuntime {
  /** Prefijo de las llamadas a la API. `/api/v1` (mismo origen) o una URL absoluta. */
  apiBase: string
  /** Origen de datos por defecto: `live` o `mock`. */
  apiMode: string
}

let cargada: Partial<ConfigRuntime> = {}

/**
 * Lee `/config.json`. No lanza nunca: cualquier fallo deja la configuración
 * vacía y quien la consulta se queda con su valor de compilación.
 */
export async function cargarConfig(): Promise<void> {
  try {
    // `no-store` y no `no-cache`: este fichero cambia cuando cambia el
    // despliegue, y una copia cacheada apuntaría a la API del entorno anterior.
    const res = await fetch('/config.json', { cache: 'no-store' })
    if (!res.ok) return

    // Cinturón, además del `location = /config.json` del nginx: si alguien lo
    // quitara, el fallback de SPA devolvería `index.html` con un 200 y el
    // `res.json()` reventaría con un error que no menciona ni al nginx ni al
    // fichero que falta.
    if (!(res.headers.get('content-type') ?? '').includes('json')) return

    const datos: unknown = await res.json()
    if (datos && typeof datos === 'object') cargada = datos as Partial<ConfigRuntime>
  } catch {
    /* Sin fichero, sin red o con JSON corrupto: se usa `import.meta.env`. Es el
       caso normal en desarrollo y no merece ni un aviso por consola. */
  }
}

/** Cadena vacía cuando no hay valor, para que el `||` de quien llama funcione. */
export function conf(clave: keyof ConfigRuntime): string {
  const valor = cargada[clave]
  return typeof valor === 'string' ? valor.trim() : ''
}
